import { useEffect } from 'react';

import { equals } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { mappedSearch } from '../actions/mapped-search';
import { getCanonical } from '../helpers/seo';
import { RootState } from '../reducers';
import {
  getCurrentPage,
  getFacets,
  getFullResultsByTheirOwnIdentifier,
  getNextStatus,
  getOptionsFilters,
  getPreviousStatus,
  getResultsCount,
  getTotalPage,
  hasReachedEnd,
  isAnyPageError,
  isAnyPageLoading,
} from '../selectors/search';

const useConnectedGrid = <T = any>(
  key: string,
  page: number,
  filters: Record<string, any> = {},
  excludedFromFacetting: string[] = [],
  keySuffix: string = null,
  reset = true
) => {
  const dispatch = useDispatch();

  const searchKey = keySuffix ? `${key}-${keySuffix}` : key;

  const data: T = useSelector(getFullResultsByTheirOwnIdentifier(searchKey));
  const isLoading: boolean = useSelector((state: RootState) =>
    isAnyPageLoading(searchKey, state)
  );
  const error = useSelector((state: RootState) =>
    isAnyPageError(searchKey, state)
  );
  const previousStatus = useSelector((state: RootState) =>
    getPreviousStatus(searchKey, state)
  );
  const nextStatus = useSelector((state: RootState) =>
    getNextStatus(searchKey, state)
  );
  const isEndReached = useSelector((state: RootState) =>
    hasReachedEnd(searchKey, state)
  );
  const count = useSelector((state: RootState) =>
    getResultsCount(searchKey, state)
  );
  const facets: Record<string, any> = useSelector((state: RootState) =>
    getFacets(searchKey, state)
  );
  const totalPage = useSelector((state: RootState) =>
    getTotalPage(searchKey, state)
  );
  const currentPage: number =
    useSelector<RootState, number>(getCurrentPage(searchKey)) || 1;
  const canonical = useSelector((state: RootState) =>
    getCanonical(state.locale, state.main?.document)
  );
  const stateFilters = useSelector((state: RootState) =>
    getOptionsFilters(searchKey, state)
  );
  useEffect((): void => {
    if (
      !isLoading &&
      !error &&
      (!equals(filters, stateFilters) || page !== currentPage)
    ) {
      dispatch(
        mappedSearch[key](
          searchKey,
          page,
          { query: filters },
          reset,
          excludedFromFacetting
        )
      );
    }
  }, [
    dispatch,
    error,
    filters,
    isLoading,
    searchKey,
    page,
    currentPage,
    stateFilters,
    reset,
  ]);

  return {
    data,
    isLoading,
    error,
    previousStatus,
    nextStatus,
    hasReachedEnd: isEndReached,
    count,
    facets,
    totalPage,
    canonical,
    page,
    filters,
  };
};

export default useConnectedGrid;
